/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1779235200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed May 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "movable-ink",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Movable Ink",
          "depth": 0,
          "id": "connect-to-movable-ink"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Sync events",
          "depth": 1,
          "id": "sync-events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      ol: "ol",
      li: "li",
      ul: "ul",
      code: "code",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#sync-events",
                children: _jsx(_components.strong, {
                  children: "Event"
                })
              })
            }), _jsx(_components.td, {
              children: "Send behavioral events to Movable Ink's Customer Data API."
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://learning.movableink.com/",
                children: "Customer Data API"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-movable-ink",
        children: "Connect to Movable Ink"
      }), "\n", _jsx(_components.p, {
        children: "To connect Movable Ink to Hightouch you need an endpoint URL, an access key, and an access secret. Movable Ink's Client Experience team provisions all three. Hightouch authenticates each request with HTTP Basic auth."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to the ", _jsx(_components.a, {
              href: "https://app.hightouch.com/destinations",
              children: "Destinations overview"
            }), " page and select ", _jsx(_components.strong, {
              children: "Add destination"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Movable Ink"
            }), " from the destination catalog."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Enter the following credentials, then select ", _jsx(_components.strong, {
              children: "Continue"
            }), ":"]
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Endpoint URL"
              }), ": the Customer Data API endpoint provided by Movable Ink. The shape is ", _jsx(_components.code, {
                children: "https://collector.micpn.com/behavioral/<key>"
              }), " for US accounts or ", _jsx(_components.code, {
                children: "https://collector.micpn-eu.com/behavioral/<key>"
              }), " for EU accounts, where ", _jsx(_components.code, {
                children: "<key>"
              }), " is your account-specific path segment."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Access key"
              }), ": the username portion of HTTP Basic auth."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Access secret"
              }), ": the password portion of HTTP Basic auth."]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Contact your Movable Ink Client Experience team if you don't have an endpoint\nURL or credentials yet. Movable Ink's developer reference\n(", _jsx(_components.code, {
            children: "learning.movableink.com"
          }), ") requires a customer login."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "sync-events",
        children: "Sync events"
      }), "\n", _jsx(_components.p, {
        children: "Send behavioral events to Movable Ink. Hightouch inserts one event per row in the model's query results each time the sync runs."
      }), "\n", _jsx(_components.h4, {
        id: "event-type",
        children: "Event type"
      }), "\n", _jsx(_components.p, {
        children: "Select the Movable Ink event type for this sync:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Event type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Page View (Product Viewed)"
              })
            }), _jsx(_components.td, {
              children: "A user viewed a product page."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Add to Cart (Product Added)"
              })
            }), _jsx(_components.td, {
              children: "A user added a product to their cart."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Remove from Cart (Product Removed)"
              })
            }), _jsx(_components.td, {
              children: "A user removed a product from their cart."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Search Completed"
              })
            }), _jsx(_components.td, {
              children: "A user submitted a search query."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Category Viewed"
              })
            }), _jsx(_components.td, {
              children: "A user viewed a category page."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Order Completed (Conversion)"
              })
            }), _jsx(_components.td, {
              children: "A user completed a purchase."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Identify"
              })
            }), _jsx(_components.td, {
              children: "An identity event, used to associate a user with timezone or other profile data."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Custom"
              })
            }), _jsxs(_components.td, {
              children: ["A custom event. You provide the ", _jsx(_components.code, {
                children: "event_type"
              }), " name and any additional fields."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "identifiers",
        children: "Identifiers"
      }), "\n", _jsx(_components.p, {
        children: "Map at least one identifier per event:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Destination field"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "User ID"
              })
            }), _jsxs(_components.td, {
              children: ["Must match the ", _jsx(_components.code, {
                children: "mi_u"
              }), " value in your Movable Ink Studio account."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Anonymous ID"
              })
            }), _jsx(_components.td, {
              children: "Used when no user ID is available."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Rows with neither a User ID nor an Anonymous ID are rejected without aborting the sync. You can review rejected rows in the ", _jsx(_components.a, {
          href: "#live-debugger",
          children: "Live debugger"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsx(_components.p, {
        children: "Map a model column to the event timestamp. If left unmapped, Hightouch defaults to the time the sync sent the event. Hightouch validates that the timestamp is no more than 30 days in the past and no more than 1 day in the future. Events outside that window are rejected."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "The mappable fields depend on the event type:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Event type"
            }), _jsx(_components.th, {
              children: "Required fields"
            }), _jsx(_components.th, {
              children: "Optional fields"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Page View / Add to Cart / Remove from Cart"
              })
            }), _jsx(_components.td, {
              children: "Product ID"
            }), _jsx(_components.td, {
              children: "Title, Price, URL, Image URL, Categories, Channel, Timezone"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Search Completed"
              })
            }), _jsx(_components.td, {
              children: "Query"
            }), _jsx(_components.td, {
              children: "URL, Channel, Timezone"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Category Viewed"
              })
            }), _jsx(_components.td, {
              children: "Category ID"
            }), _jsx(_components.td, {
              children: "Category URL, Channel, Timezone"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Order Completed"
              })
            }), _jsx(_components.td, {
              children: "Products"
            }), _jsx(_components.td, {
              children: "Order ID, Revenue, Discount, Coupon Code, Channel, Timezone"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Identify"
              })
            }), _jsx(_components.td, {
              children: "—"
            }), _jsx(_components.td, {
              children: "Timezone"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Custom"
              })
            }), _jsxs(_components.td, {
              children: ["Event name (", _jsx(_components.code, {
                children: "event_type"
              }), ")"]
            }), _jsx(_components.td, {
              children: "Any additional fields you map"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For ", _jsx(_components.strong, {
          children: "Order Completed"
        }), ", the ", _jsx(_components.strong, {
          children: "Products"
        }), " field expects an array of product objects. Use Hightouch's ", _jsx(_components.a, {
          href: "/syncs/mapping-data#inline-mapping",
          children: "inline mapper"
        }), " or an ", _jsx(_components.a, {
          href: "/syncs/mapping-data#create-an-array",
          children: "array column"
        }), " from your model."]
      }), "\n", _jsxs(_components.p, {
        children: ["For ", _jsx(_components.strong, {
          children: "Custom"
        }), " events, Movable Ink supports up to 10 unique custom event names per account by default. Contact your Movable Ink rep if you need more."]
      }), "\n", _jsx(_components.h4, {
        id: "payload-limits",
        children: "Payload limits"
      }), "\n", _jsx(_components.p, {
        children: "Movable Ink rejects events that exceed its payload limits. Hightouch checks these before sending and rejects offending rows:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Any string field longer than 256 characters."
        }), "\n", _jsx(_components.li, {
          children: "A total event payload larger than 32 KB."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Rejected rows are surfaced in the ", _jsx(_components.a, {
          href: "#live-debugger",
          children: "Live debugger"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first sync completes, confirm the events landed in Movable Ink:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Ask your Movable Ink Client Experience team to confirm events are arriving on the configured endpoint. Movable Ink's developer site (", _jsx(_components.code, {
            children: "learning.movableink.com"
          }), ") is customer-login-gated, so direct verification from Hightouch's side ends at a successful HTTP 2xx response from the Customer Data API."]
        }), "\n", _jsxs(_components.li, {
          children: ["Check the ", _jsx(_components.a, {
            href: "#live-debugger",
            children: "Live debugger"
          }), " for rejected rows. The most common rejections are missing identifiers, missing event names on custom events, out-of-window timestamps, oversized string fields, and oversized payloads."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
